import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Button, Checkbox, Input, Modal } from '@/components/ui'
import { PageTitle } from '@/components'
import { cn } from '@/lib/utils'
import {
  RefundCheckin,
  RefundCheckinRequest,
  RefundCryptoOrder,
  RefundExtraPayload,
  RefundExtraRequest,
  RefundOrder,
  RefundOrderPayload,
  RefundParticipationTicket,
} from '../../../common/models/orders'
import FormGroup from '../../../core/components/FormGroup'
import { updateRefundOrder } from '../../../core/services/orders'
import { MessageBox } from '../messages/MessageBox'
import TextInput from '@/components/text-input.tsx'

export interface ICancelOrderValues {
  tickets: {
    ticketPriceEnabled: boolean
    refundAmountDefaultCurrency: number
    userParticipationTicketId: string
    bagTicketId: string
    extras?: {
      extraId: string
      extraPriceEnabled: boolean
      refundAmountDefaultCurrency: number
    }[]
    checkins?: {
      id: string
      checkinPriceEnabled: boolean
      refundAmountDefaultCurrency: number
    }[]
    installmentsFee?: {
      bagTicketId?: string | undefined
      priceEnabled: boolean
      refundAmountDefaultCurrency: number
      availableAmountDefaultCurrency: number
      refundedAmountDefaultCurrency: number
    }
    cancelTicket: boolean
  }[]
  runa: 'true' | 'false'
}

export interface CancelOrderModalProps {
  showCancelModal: boolean
  setShowCancelModal: (show: boolean) => void
  order?: RefundOrder
  withRuna: boolean
  orderId: string
  currency: string
}

export const CancelOrderModal = (
  props: CancelOrderModalProps,
): JSX.Element | null => {
  const {
    showCancelModal,
    withRuna,
    setShowCancelModal,
    order,
    orderId,
    currency,
  } = props

  const [fullRefund, setFullRefund] = useState<number>(0)
  const [selectedTickets, setSelectedTickets] = useState<
    RefundParticipationTicket[]
  >([])

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const schema = yup.object().shape({
    tickets: yup
      .array()
      .min(1)
      .of(
        yup.object().shape({
          userParticipationTicketId: yup.string().required(),
          bagTicketId: yup.string().required(),
          ticketPriceEnabled: yup.boolean().required(),
          refundAmountDefaultCurrency: yup.number().required(),
          extras: yup
            .array()
            .of(
              yup.object().shape({
                extraId: yup.string().required(),
                extraPriceEnabled: yup.boolean().required(),
                refundAmountDefaultCurrency: yup.number().required(),
              }),
            )
            .optional(),
          checkins: yup
            .array()
            .of(
              yup.object().shape({
                id: yup.string().required(),
                checkinPriceEnabled: yup.boolean().required(),
                refundAmountDefaultCurrency: yup.number().required(),
              }),
            )
            .optional(),
          installmentsFee: yup
            .object()
            .shape({
              bagTicketId: yup.string().optional(),
              priceEnabled: yup.boolean().required(),
              refundAmountDefaultCurrency: yup.number().required(),
              availableAmountDefaultCurrency: yup.number().required(),
              refundedAmountDefaultCurrency: yup.number().required(),
            })
            .optional(),
          cancelTicket: yup.boolean().required(),
        }),
      )
      .required(),
    runa: yup.string().oneOf(['true', 'false']).required(),
  })

  const {
    handleSubmit,
    register,
    reset,
    control,
    formState: { errors, isSubmitting },
    setValue,
    watch,
  } = useForm<ICancelOrderValues>({
    mode: 'onChange',
    resolver: yupResolver<ICancelOrderValues>(schema),
  })

  const { append: ticketAppend, remove: ticketRemove } = useFieldArray({
    control, // control props comes from useForm (optional: if you are using FormContext)
    name: 'tickets', // unique name for your Field Array
  })

  const ticketWatch = watch()

  useEffect(() => {
    if (order) {
      let finalSum = 0
      const tickets = watch(`tickets`, [])
      let index = 0
      for (const ticket of tickets) {
        if (ticket && ticket.ticketPriceEnabled) {
          finalSum += ticket.refundAmountDefaultCurrency
          if (ticket.extras) {
            for (const extra of ticket.extras) {
              if (extra && extra.extraPriceEnabled) {
                finalSum += extra.refundAmountDefaultCurrency
              }
            }
          }
          if (ticket.checkins) {
            for (const checkin of ticket.checkins) {
              if (checkin && checkin.checkinPriceEnabled) {
                finalSum += checkin.refundAmountDefaultCurrency
              }
            }
          }
        }
        const installmentsFee = watch(`tickets.${index}.installmentsFee`)
        if (
          installmentsFee &&
          installmentsFee.bagTicketId &&
          installmentsFee.priceEnabled
        ) {
          finalSum += installmentsFee.refundAmountDefaultCurrency
        }
        index++
      }
      setFullRefund(parseFloat(finalSum.toFixed(2)))
    }
  }, [ticketWatch, selectedTickets])

  const handleClose = () => {
    reset()
    setShowCancelModal(false)
  }

  const submit = async (values: ICancelOrderValues) => {
    const tickets = watch(`tickets`, [])
    const payload: RefundOrderPayload = {
      refundBagTickets: [],
      type: withRuna ? 'COINS' : 'CARD',
    }

    let index = 0
    for (const ticket of tickets) {
      if (ticket) {
        const refundBagTicket = payload.refundBagTickets.filter(
          (t) => t.bagTicketId === ticket.bagTicketId,
        )
        const extras: RefundExtraRequest[] = []
        const checkins: RefundCheckinRequest[] = []

        if (ticket.extras) {
          for (const extra of ticket.extras) {
            if (
              extra &&
              extra.extraPriceEnabled &&
              extra.refundAmountDefaultCurrency > 0
            ) {
              extras.push({
                partialUpgradeId: extra.extraId,
                amountDefaultCurrency: extra.extraPriceEnabled
                  ? extra.refundAmountDefaultCurrency
                  : 0,
              })
            }
          }
        }

        if (ticket.checkins) {
          for (const checkin of ticket.checkins) {
            if (
              checkin &&
              checkin.checkinPriceEnabled &&
              checkin.refundAmountDefaultCurrency > 0
            ) {
              checkins.push({
                checkinId: checkin.id,
                amountDefaultCurrency: checkin.checkinPriceEnabled
                  ? checkin.refundAmountDefaultCurrency
                  : 0,
                cancelCheckin: checkin.checkinPriceEnabled,
              })
            }
          }
        }

        if (refundBagTicket.length > 0) {
          refundBagTicket[0].participationTickets.push({
            userParticipationTicketId: ticket.userParticipationTicketId,
            refundAmountDefaultCurrency: ticket.ticketPriceEnabled
              ? ticket.refundAmountDefaultCurrency
              : 0,
            refundExtras: extras,
            refundCheckins: checkins,
            cancelTicket: watch(`tickets.${index}.cancelTicket`) || false,
          })
        } else {
          let refundInstallmentFeeAmountDefaultCurrency = 0
          if (watch(`tickets.${index}.installmentsFee.priceEnabled`) || 0) {
            refundInstallmentFeeAmountDefaultCurrency =
              watch(
                `tickets.${index}.installmentsFee.refundAmountDefaultCurrency`,
              ) || 0
          }
          payload.refundBagTickets.push({
            bagTicketId: ticket.bagTicketId,
            refundInstallmentFeeAmountDefaultCurrency:
              refundInstallmentFeeAmountDefaultCurrency,
            participationTickets: [
              {
                userParticipationTicketId: ticket.userParticipationTicketId,
                refundAmountDefaultCurrency: ticket.ticketPriceEnabled
                  ? ticket.refundAmountDefaultCurrency
                  : 0,
                refundExtras: extras,
                refundCheckins: checkins,
                cancelTicket: watch(`tickets.${index}.cancelTicket`) || false,
              },
            ],
          })
        }
        index++
      }
    }

    try {
      const { status } = await updateRefundOrder(orderId, payload)
      if (status === 200) {
        setIsError(false)
        setShowMessageBox(true)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
    handleClose()
  }

  useEffect(() => {
    if (order) {
      setValue('runa', withRuna ? 'true' : 'false')
    }

    return () => {
      reset()
    }
  }, [order, showCancelModal])

  if (!order) {
    return null
  }

  const CryptoOrderModel = (model: { cryptoOrder: RefundCryptoOrder }) => {
    return (
      <>
        <FormGroup label={'Blockchain'}>
          <Input disabled value={model.cryptoOrder.chainId} />
        </FormGroup>
        <FormGroup label={'Transaction ID'}>
          <Input disabled value={model.cryptoOrder.paymentAddress} />
        </FormGroup>
      </>
    )
  }

  return (
    <>
      <Modal
        open={showCancelModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Cancel & Refund Order" />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            <Button onClick={handleClose} type="button" variant="secondary">
              Close
            </Button>
            <Button
              form="cancel-order-form"
              type="submit"
              variant="destructive"
              disabled={isSubmitting}
              loading={isSubmitting}
            >
              Cancel & Refund
            </Button>
          </div>
        }
      >
        <div className="text-l font-semibold tracking-tight text-monochrome-1 mb-5">
          {orderId}
        </div>
        <form
          id="cancel-order-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex flex-col items-start gap-3 border-b border-monochrome-120 pb-4">
            <div
              className={cn(
                'flex w-full flex-col gap-2 rounded-[10px] bg-monochrome-120 p-4',
              )}
            >
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  {...register('runa')}
                  value="true"
                  className="h-4 w-4"
                />
                <span className="text-sm text-monochrome-1">
                  Cancel & refund with RUNA
                </span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  {...register('runa')}
                  value="false"
                  className="h-4 w-4"
                />
                <span className="text-sm text-monochrome-1">
                  Cancel without RUNA
                </span>
              </label>
              {errors?.runa?.message && (
                <span className="text-sm text-accent-3">
                  {errors.runa.message}
                </span>
              )}
            </div>

            <FormGroup
              labelClassName="!text-body-large"
              labelWrapperClassName="mt-3 mb-3"
              label="Tickets"
            >
              <div
                className={cn(
                  'flex w-full flex-col gap-2 rounded-[10px] bg-monochrome-120 p-4',
                )}
              >
                {order?.bagTickets &&
                  order?.bagTickets.map((bagTicket, bagTicketIndex) => {
                    return bagTicket.participationTickets.map(
                      (tick, participationTicketIndex) => {
                        return (
                          <label
                            key={tick.userParticipationTicketId}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <Controller
                              name={`tickets.${
                                bagTicketIndex + participationTicketIndex
                              }`}
                              control={control}
                              render={({ field }) => (
                                <Checkbox
                                  checked={selectedTickets.includes(tick)}
                                  onCheckedChange={(checked) => {
                                    if (
                                      selectedTickets.find((t) => t === tick)
                                    ) {
                                      setSelectedTickets([
                                        ...selectedTickets.filter(
                                          (t) => t !== tick,
                                        ),
                                      ])
                                      ticketRemove(
                                        selectedTickets.findIndex(
                                          (t) =>
                                            tick.userParticipationTicketId ===
                                            t.userParticipationTicketId,
                                        ),
                                      )
                                    } else {
                                      setSelectedTickets([
                                        ...selectedTickets,
                                        tick,
                                      ])
                                      ticketAppend({
                                        userParticipationTicketId:
                                          tick.userParticipationTicketId,
                                        ticketPriceEnabled: false,
                                        refundAmountDefaultCurrency:
                                          tick.availableForRefundAmountDefaultCurrency,
                                        bagTicketId: bagTicket.bagTicketId,
                                        extras: tick.refundExtras.map((e) => ({
                                          extraId: e.partialUpgradeId,
                                          extraPriceEnabled: false,
                                          refundAmountDefaultCurrency:
                                            e.availableForRefundAmountDefaultCurrency,
                                        })),
                                        checkins: tick.refundCheckins.map(
                                          (c) => ({
                                            id: c.id,
                                            checkinPriceEnabled: false,
                                            refundAmountDefaultCurrency:
                                              c.availableForRefundAmountDefaultCurrency,
                                          }),
                                        ),
                                        installmentsFee: {
                                          bagTicketId:
                                            participationTicketIndex === 0
                                              ? bagTicket.bagTicketId
                                              : undefined,
                                          priceEnabled: false,
                                          refundAmountDefaultCurrency:
                                            bagTicket.installmentsFee
                                              .availableAmountDefaultCurrency,
                                          availableAmountDefaultCurrency:
                                            bagTicket.installmentsFee
                                              .availableAmountDefaultCurrency,
                                          refundedAmountDefaultCurrency:
                                            bagTicket.installmentsFee
                                              .refundedAmountDefaultCurrency,
                                        },
                                        cancelTicket: false,
                                      })
                                    }
                                  }}
                                />
                              )}
                            />
                            <span className="text-sm font-bold text-monochrome-1">
                              {tick.ticketCode} - {tick.boughtForName}
                            </span>
                          </label>
                        )
                      },
                    )
                  })}
              </div>
            </FormGroup>

            {selectedTickets.map((ticket, i) => {
              return (
                <>
                  <FormGroup
                    labelClassName="!text-body-large"
                    labelWrapperClassName="mt-3 mb-3"
                    label={`${ticket.ticketCode} - ${ticket.boughtForName}`}
                  >
                    <div className={cn('flex w-full flex-col gap-3')}>
                      {watch(`tickets.${i}.installmentsFee.bagTicketId`) &&
                        (watch(
                          `tickets.${i}.installmentsFee.refundAmountDefaultCurrency`,
                        ) || 0) > 0 && (
                          <div
                            className={cn(
                              'flex w-full flex-col gap-3 rounded-[10px] bg-monochrome-120 p-4',
                            )}
                          >
                            <FormGroup
                              direction="row"
                              wrapperClassName="justify-content-end"
                              inputWrapperClassName="w-auto"
                            >
                              <>
                                <Controller
                                  name={`tickets.${i}.installmentsFee.priceEnabled`}
                                  control={control}
                                  render={() => (
                                    <Checkbox
                                      checked={Boolean(
                                        watch(
                                          `tickets.${i}.installmentsFee.priceEnabled`,
                                        ),
                                      )}
                                      onCheckedChange={() => {
                                        setValue(
                                          `tickets.${i}.installmentsFee.priceEnabled`,
                                          !watch(
                                            `tickets.${i}.installmentsFee.priceEnabled`,
                                          ),
                                        )
                                      }}
                                    />
                                  )}
                                />
                                <span className="text-sm text-monochrome-1">{`Installment fee ${currency}${
                                  (watch(
                                    `tickets.${i}.installmentsFee.refundedAmountDefaultCurrency`,
                                  ) || 0) > 0
                                    ? ` (${
                                        (watch(
                                          `tickets.${i}.installmentsFee.availableAmountDefaultCurrency`,
                                        ) || 0) > 0
                                          ? 'Partially refunded'
                                          : 'Refunded'
                                      } ${
                                        watch(
                                          `tickets.${i}.installmentsFee.refundedAmountDefaultCurrency`,
                                        ) || 0
                                      } ${currency})`
                                    : ''
                                }`}</span>
                              </>
                            </FormGroup>
                            <FormGroup label={''}>
                              <Input
                                disabled={
                                  !Boolean(
                                    watch(
                                      `tickets.${i}.installmentsFee.priceEnabled`,
                                    ),
                                  )
                                }
                                type="number"
                                value={Number(
                                  watch(
                                    `tickets.${i}.installmentsFee.refundAmountDefaultCurrency`,
                                  ),
                                )}
                                onChange={(event) => {
                                  if (
                                    +event.target.value <=
                                    (watch(
                                      `tickets.${i}.installmentsFee.availableAmountDefaultCurrency`,
                                    ) || 0)
                                  ) {
                                    setValue(
                                      `tickets.${i}.installmentsFee.refundAmountDefaultCurrency`,
                                      +event.target.value,
                                    )
                                  } else {
                                    setValue(
                                      `tickets.${i}.installmentsFee.refundAmountDefaultCurrency`,
                                      +(
                                        watch(
                                          `tickets.${i}.installmentsFee.availableAmountDefaultCurrency`,
                                        ) || 0
                                      ),
                                    )
                                  }
                                }}
                              />
                            </FormGroup>
                            {ticket.cryptoPayment && (
                              <CryptoOrderModel
                                cryptoOrder={ticket.cryptoOrder}
                              />
                            )}
                          </div>
                        )}
                      <div
                        className={cn(
                          'flex w-full flex-col gap-3 rounded-[10px] bg-monochrome-120 p-4',
                        )}
                      >
                        <FormGroup
                          direction="row"
                          wrapperClassName="justify-content-end"
                          inputWrapperClassName="w-auto"
                        >
                          <>
                            <Controller
                              name={`tickets.${i}.ticketPriceEnabled`}
                              control={control}
                              render={() => (
                                <Checkbox
                                  checked={Boolean(
                                    watch(`tickets.${i}.ticketPriceEnabled`),
                                  )}
                                  onCheckedChange={() => {
                                    setValue(
                                      `tickets.${i}.ticketPriceEnabled`,
                                      !watch(`tickets.${i}.ticketPriceEnabled`),
                                    )
                                  }}
                                />
                              )}
                            />
                            <span className="text-sm text-monochrome-1">{`Tickets Total Price in ${currency}${
                              ticket.refundedAmountDefaultCurrency > 0
                                ? ` (${
                                    ticket.availableForRefundAmountDefaultCurrency >
                                    0
                                      ? 'Partially refunded'
                                      : 'Refunded'
                                  } ${
                                    ticket.refundedAmountDefaultCurrency
                                  } ${currency})`
                                : ''
                            }`}</span>
                          </>
                        </FormGroup>
                        <FormGroup label={`Tickets Total Price in ${currency}`}>
                          <Input
                            disabled={
                              !Boolean(watch(`tickets.${i}.ticketPriceEnabled`))
                            }
                            type="number"
                            value={Number(
                              watch(`tickets.${i}.refundAmountDefaultCurrency`),
                            )}
                            onChange={(event) => {
                              if (
                                +event.target.value <=
                                ticket.availableForRefundAmountDefaultCurrency
                              ) {
                                setValue(
                                  `tickets.${i}.refundAmountDefaultCurrency`,
                                  +event.target.value,
                                )
                              } else {
                                setValue(
                                  `tickets.${i}.refundAmountDefaultCurrency`,
                                  +ticket.availableForRefundAmountDefaultCurrency,
                                )
                              }
                            }}
                          />
                        </FormGroup>
                        {ticket.cryptoPayment && (
                          <CryptoOrderModel cryptoOrder={ticket.cryptoOrder} />
                        )}
                      </div>
                    </div>
                  </FormGroup>
                  {ticket.refundExtras.map((extra, j) => {
                    return (
                      <div
                        className={cn(
                          'flex w-full flex-col gap-3 rounded-[10px] bg-monochrome-120 p-4',
                        )}
                      >
                        <FormGroup
                          direction="row"
                          wrapperClassName="justify-content-end"
                          inputWrapperClassName="w-auto"
                        >
                          <>
                            <Controller
                              name={`tickets.${i}.extras.${j}.extraPriceEnabled`}
                              control={control}
                              render={() => (
                                <Checkbox
                                  checked={Boolean(
                                    watch(
                                      `tickets.${i}.extras.${j}.extraPriceEnabled`,
                                    ),
                                  )}
                                  onCheckedChange={() => {
                                    setValue(
                                      `tickets.${i}.extras.${j}.extraPriceEnabled`,
                                      !watch(
                                        `tickets.${i}.extras.${j}.extraPriceEnabled`,
                                      ),
                                    )
                                  }}
                                />
                              )}
                            />
                            <span className="text-sm text-monochrome-1">{`${
                              extra.name
                            }${
                              extra.refundedAmountDefaultCurrency > 0
                                ? ` (${
                                    ticket.availableForRefundAmountDefaultCurrency >
                                    0
                                      ? 'Partially refunded'
                                      : 'Refunded'
                                  } ${
                                    ticket.refundedAmountDefaultCurrency
                                  } ${currency})`
                                : ''
                            }`}</span>
                          </>
                        </FormGroup>
                        <FormGroup label={`Price`}>
                          <Input
                            disabled={
                              !Boolean(
                                watch(
                                  `tickets.${i}.extras.${j}.extraPriceEnabled`,
                                ),
                              )
                            }
                            type="number"
                            value={Number(
                              watch(
                                `tickets.${i}.extras.${j}.refundAmountDefaultCurrency`,
                              ),
                            )}
                            onChange={(event) => {
                              if (
                                +event.target.value <=
                                extra.availableForRefundAmountDefaultCurrency
                              ) {
                                setValue(
                                  `tickets.${i}.extras.${j}.refundAmountDefaultCurrency`,
                                  +event.target.value,
                                )
                              } else {
                                setValue(
                                  `tickets.${i}.extras.${j}.refundAmountDefaultCurrency`,
                                  +extra.availableForRefundAmountDefaultCurrency,
                                )
                              }
                            }}
                          />
                        </FormGroup>
                      </div>
                    )
                  })}
                  {ticket.refundCheckins.length > 0 && (
                    <div
                      className={cn(
                        'flex w-full flex-col gap-3 rounded-[10px]  bg-monochrome-120 p-4 text-monochrome-1',
                      )}
                    >
                      Check-ins:
                      {ticket.refundCheckins.map((checkin, j) => {
                        return (
                          <div
                            className={cn(
                              'flex w-full flex-col gap-3 rounded-[10px]  bg-monochrome-120 p-4',
                            )}
                          >
                            <FormGroup
                              direction="row"
                              wrapperClassName="justify-content-end"
                              inputWrapperClassName="w-auto"
                            >
                              <>
                                <Controller
                                  name={`tickets.${i}.checkins.${j}.checkinPriceEnabled`}
                                  control={control}
                                  render={() => (
                                    <Checkbox
                                      checked={Boolean(
                                        watch(
                                          `tickets.${i}.checkins.${j}.checkinPriceEnabled`,
                                        ),
                                      )}
                                      onCheckedChange={() => {
                                        setValue(
                                          `tickets.${i}.checkins.${j}.checkinPriceEnabled`,
                                          !watch(
                                            `tickets.${i}.checkins.${j}.checkinPriceEnabled`,
                                          ),
                                        )
                                      }}
                                    />
                                  )}
                                />
                                <span className="text-sm text-monochrome-1">{`${
                                  checkin.firstName
                                } ${checkin.lastName}${
                                  checkin.refundedAmountDefaultCurrency > 0
                                    ? ` (${
                                        ticket.availableForRefundAmountDefaultCurrency >
                                        0
                                          ? 'Partially refunded'
                                          : 'Refunded'
                                      } ${
                                        ticket.refundedAmountDefaultCurrency
                                      } ${currency})`
                                    : ''
                                }`}</span>
                              </>
                            </FormGroup>
                            <FormGroup label={`Total Price`}>
                              <Input
                                disabled={
                                  !Boolean(
                                    watch(
                                      `tickets.${i}.checkins.${j}.checkinPriceEnabled`,
                                    ),
                                  )
                                }
                                type="number"
                                value={Number(
                                  watch(
                                    `tickets.${i}.checkins.${j}.refundAmountDefaultCurrency`,
                                  ),
                                )}
                                onChange={(event) => {
                                  if (
                                    +event.target.value <=
                                    checkin.availableForRefundAmountDefaultCurrency
                                  ) {
                                    setValue(
                                      `tickets.${i}.checkins.${j}.refundAmountDefaultCurrency`,
                                      +event.target.value,
                                    )
                                  } else {
                                    setValue(
                                      `tickets.${i}.checkins.${j}.refundAmountDefaultCurrency`,
                                      +checkin.availableForRefundAmountDefaultCurrency,
                                    )
                                  }
                                }}
                              />
                            </FormGroup>
                          </div>
                        )
                      })}
                    </div>
                  )}
                  <FormGroup
                    inputWrapperClassName="w-auto"
                    label="Cancel Ticket"
                    direction="row"
                    labelClassName="whitespace-nowrap"
                    className="mt-2 flex flex-row-reverse"
                  >
                    <Controller
                      name={`tickets.${i}.cancelTicket`}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          className="mr-2"
                          checked={Boolean(field.value)}
                          onCheckedChange={(checked) =>
                            field.onChange(Boolean(checked))
                          }
                        />
                      )}
                    />
                  </FormGroup>
                </>
              )
            })}
          </div>
          <FormGroup
            labelClassName="!text-body-large"
            labelWrapperClassName="mb-3"
            label="Total to be refunded:"
          >
            <div className="flex items-center gap-2 w-full">
              <i
                className="bi bi-tag-fill p-0 m-0"
                style={{ fontSize: '20px' }}
              />
              <Input value={fullRefund} disabled type="number" />
            </div>
          </FormGroup>
        </form>
      </Modal>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
